import * as v from 'valibot'
import * as R from 'remeda'
import { useMemo } from 'react'
import {
  AcceptedInput,
  Content,
  ConversationAction,
} from '@chilipiper/api-client/src/chat-platform-chat'
import {
  Block,
  BookMeetingBlock,
  EnterPiperAiChatBlock,
  EventsMap,
  FillFormBlock,
  MessageBlock,
  ParsedBlock,
  ParsedPayloadIn,
  ReplyButtonsBlock,
  SystemSetupPiperAiChat,
} from './types'

const contentHandler = (
  message: Content,
  author: string,
  event: Extract<ParsedPayloadIn, { type: 'ConversationData' }>,
  index: number
): MessageBlock => {
  return {
    id: `${event.id}___content_${index}`,
    author,
    sent: true,
    type: 'Message',
    parts: message.value.map(part =>
      part.type === 'Text' ? { type: 'Text', text: part.text.trim() } : part
    ),
    statuses: event.statuses,
    timestamp: event.timestamp,
    roomId: event.roomId,
  }
}

/*eslint complexity: ["error", 24]*/
const acceptedInputHandler = (
  message: AcceptedInput,
  currBlocks: Array<ParsedBlock>
): Array<ParsedBlock> => {
  const messageValue = message.value
  switch (messageValue.type) {
    case 'ReplyButtonSelected': {
      const lastReplyButtonsIndex =
        R.findLastIndex(
          currBlocks,
          block => block.type === 'ReplyButtons' || block.type === 'EnterPiperAiChat'
        ) ?? -1
      const repliesBlock = currBlocks[lastReplyButtonsIndex] as
        | ReplyButtonsBlock
        | EnterPiperAiChatBlock

      if (!repliesBlock) {
        return currBlocks
      }

      const newBlock: ReplyButtonsBlock | EnterPiperAiChatBlock = {
        ...repliesBlock,
        selected: messageValue.index,
      }

      return currBlocks.map((block, index) => {
        if (index === lastReplyButtonsIndex) {
          return newBlock
        }
        return block
      })
    }
    case 'FormData': {
      const lastFormIndex = R.findLastIndex(currBlocks, block => block.type === 'FillForm') ?? -1
      const formDataBlock = currBlocks[lastFormIndex] as FillFormBlock

      if (!formDataBlock) {
        return currBlocks
      }

      const newBlock: FillFormBlock = {
        ...formDataBlock,
        values: messageValue.values,
      }

      return currBlocks.map((block, index) => {
        if (index === lastFormIndex) {
          return newBlock
        }
        return block
      })
    }
    case 'MeetingSlotSelectedWithReservation':
    case 'MeetingSlotSelectedWithoutReservation': {
      const lastMeetingIndex =
        R.findLastIndex(currBlocks, block => block.type === 'BookMeeting') ?? -1
      const meetingSlotBlock = currBlocks[lastMeetingIndex] as BookMeetingBlock

      if (!meetingSlotBlock) {
        return currBlocks
      }

      const newBlock: BookMeetingBlock = {
        ...meetingSlotBlock,
        email: messageValue.email,
      }

      return currBlocks.map((block, index) => {
        if (index === lastMeetingIndex) {
          return newBlock
        }
        return block
      })
    }

    case 'MeetingSlotSelectionCancelled': {
      const lastMeetingIndex =
        R.findLastIndex(currBlocks, block => block.type === 'BookMeeting') ?? -1
      const meetingSlotBlock = currBlocks[lastMeetingIndex] as BookMeetingBlock

      if (!meetingSlotBlock) {
        return currBlocks
      }

      const newBlock: BookMeetingBlock = {
        ...meetingSlotBlock,
        cancelled: true,
      }

      return currBlocks.map((block, index) => {
        if (index === lastMeetingIndex) {
          return newBlock
        }
        return block
      })
    }

    case 'MessageToPiperAi':
    case 'WaitingForRepHasTimedout':
    case 'NodeExecutionTimedout':
    case 'AiResponse':
    case 'NodeExecutionResumed':
    case 'RepSentMessage':
    case 'Start': {
      return currBlocks
    }

    default:
      messageValue satisfies never
      return currBlocks
  }
}

const conversationDataActionHandler = (
  action: ConversationAction,
  guestId: string | undefined,
  botId: string,
  event: Extract<ParsedPayloadIn, { type: 'ConversationData' }>
): { block: Block } | undefined => {
  const guest = guestId ?? 'guest'

  if (
    event.type === 'ConversationData' &&
    !!event.messages?.length &&
    event.messages.every(
      message => message.type === 'PiperAiContent' && message.metadata.status === 'InProgress'
    )
  ) {
    // If all messages are in progress, don't add Action (which is EnterPiperAiChat) because it would lead to duplicates as the same action is sent after every chunk
    return undefined
  }

  const commonData = {
    sent: true,
    id: `${event.id}___action`,
    author: guest,
    statuses: event.statuses,
    timestamp: event.timestamp,
    roomId: event.roomId,
  } satisfies Partial<Block>

  switch (action.type) {
    case 'SelectReplyAction': {
      return {
        block: {
          ...commonData,
          type: 'ReplyButtons',
          replies: action.replies,
        },
      }
    }

    case 'SubmitFormAction': {
      return {
        block: {
          ...commonData,
          type: 'FillForm',
          fields: action.fields,
        },
      }
    }

    // This event is sent when BE was unable to send 'SubmitFormAction' because of an error
    case 'SubmitFormActionError': {
      return {
        block: {
          ...commonData,
          type: 'Error',
          message: action.message,
        },
      }
    }

    case 'RepJoinOrTimeoutAction':
    case 'StartLiveChat': {
      return {
        block: {
          ...commonData,
          author: botId,
          type: 'RouteToRep',
        },
      }
    }

    case 'TerminateConversation': {
      return {
        block: {
          ...commonData,
          author: botId,
          type: 'TerminateConversation',
        },
      }
    }

    case 'SelectMeetingSlotAction': {
      return {
        block: {
          ...commonData,
          distributionId:
            action.model.assignee.type === 'Team'
              ? action.model.assignee.distributionId
              : undefined,
          type: 'BookMeeting',
          assignees: action.assignees,
          meetingTypeId: action.model.meetingType.id,
          email: action.email,
          timeout: parseTimeout(action.model.slotSelectionTimeout),
        },
      }
    }

    case 'EnterPiperAiChat': {
      return {
        block: {
          ...commonData,
          type: 'EnterPiperAiChat',
          selected: undefined,
          shortcuts: action.shortcuts,
        },
      }
    }

    case 'ResumeNodeExecution':
    case 'SelectDataFieldPicklistReplyAction':
    case 'HandledInputInPiperAiContext':
    case 'SubmitFormEntry': {
      return undefined
    }

    default: {
      action satisfies never
      return undefined
    }
  }
}

const TimeoutSchema = v.object({
  number: v.pipe(v.number(), v.minValue(0)),
  unit: v.picklist(['minutes', 'seconds', 'minute', 'second']),
})

/**
 * Parses a timeout string into milliseconds
 *
 * Supports only "minutes", "minute", "seconds" and "second" units
 *
 * @example
 *   parseTimeout('10 minutes') // 600000
 *   parseTimeout('1 second') // 1000
 */
const parseTimeout = (timeout?: string) => {
  const match = timeout?.match(/(\d+)\s*(\w+)/)

  if (match) {
    const { success, output } = v.safeParse(TimeoutSchema, {
      number: parseInt(match[1] as string, 10),
      unit: match[2],
    })
    if (success) {
      if (output.unit === 'seconds' || output.unit === 'second') {
        return output.number * 1000
      }
      if (output.unit === 'minutes' || output.unit === 'minute') {
        return output.number * 60 * 1000
      }
    }
  }
}

const getStreamedMessageId = (messageId: number) => `streamed_message_${messageId}`

/**
 * Consider that a ConversationData event may contain messages, a request for user input and also
 * previous user input in case user replied to a block. This function will break them down into
 * individual blocks.
 */
const assembleBlocks = (events: ParsedPayloadIn[], guestId: string | undefined, botId: string) => {
  let parsedEvents: Array<ParsedBlock> = []

  // eslint-disable-next-line complexity
  events.forEach(event => {
    if (event.type === 'ConversationData') {
      event.messages?.forEach((message, index) => {
        if (message.type === 'Content') {
          const contentBlock = contentHandler(message, botId, event, index)
          parsedEvents.push(contentBlock)
        }
        if (message.type === 'PiperAiContent') {
          if (message.metadata.offset === 0) {
            parsedEvents.push({
              id: getStreamedMessageId(message.metadata.messageId),
              author: botId,
              sent: true,
              type: 'PiperAiMessage',
              parts: [
                message.value.type === 'Text'
                  ? { type: 'Text', text: message.value.text }
                  : message.value,
              ],
              isComplete: message.metadata.status === 'Completed',
              statuses: event.statuses,
              timestamp: event.timestamp,
              roomId: event.roomId,
            })
          } else {
            const eventToModify = parsedEvents.findLast(
              event => event.id === getStreamedMessageId(message.metadata.messageId)
            )
            if (eventToModify?.type === 'PiperAiMessage') {
              const lastPart = eventToModify.parts.at(-1)
              if (lastPart?.type === 'Text' && message.value.type === 'Text') {
                lastPart.text += message.value.text
              } else {
                eventToModify.parts.push(message.value)
              }
              eventToModify.isComplete = message.metadata.status === 'Completed'
            }
          }
        }

        if (message.type === 'AcceptedInput') {
          parsedEvents = acceptedInputHandler(message, parsedEvents)
        }
      })

      const actionResult = conversationDataActionHandler(event.action, guestId, botId, event)

      if (actionResult) {
        parsedEvents.push(actionResult.block)
      }
    } else if (
      event.type === 'SystemMessage' &&
      event.payload?.type === 'MeetingSchedulingRequested'
    ) {
      const lastMeetingIndex =
        R.findLastIndex(parsedEvents, block => block.type === 'BookMeeting') ?? -1
      const meetingSlotBlock = parsedEvents[lastMeetingIndex] as BookMeetingBlock

      if (meetingSlotBlock) {
        const newBlock: BookMeetingBlock = {
          ...meetingSlotBlock,
          meetingId: event.payload.meetingId,
        }

        parsedEvents = parsedEvents.map((block, index) => {
          if (index === lastMeetingIndex) {
            return newBlock
          }
          return block
        })
      }
    } else if (event.type === 'ReplyButtonSelected') {
      const lastReplyButtonsIndex =
        R.findLastIndex(
          parsedEvents,
          block => block.type === 'ReplyButtons' || block.type === 'EnterPiperAiChat'
        ) ?? -1
      const repliesBlock = parsedEvents[lastReplyButtonsIndex] as
        | ReplyButtonsBlock
        | EnterPiperAiChatBlock

      parsedEvents = parsedEvents.map((block, index) => {
        if (index === lastReplyButtonsIndex && repliesBlock) {
          return {
            ...repliesBlock,
            selected: event.index,
          }
        }
        return block
      })
    } else if (event.type === 'SystemMessage' && event.payload.type === 'SetupPiperAiChat') {
      parsedEvents.push({
        ...event,
        payload: {
          ...event.payload,
          isSetupComplete: false,
          isSetupFailed: false,
        },
      } as SystemSetupPiperAiChat)
    } else if (event.type === 'SystemMessage' && event.payload.type === 'MeetingSchedulingFailed') {
      const lastMeetingIndex =
        R.findLastIndex(parsedEvents, block => block.type === 'BookMeeting') ?? -1
      const meetingSlotBlock = parsedEvents[lastMeetingIndex] as BookMeetingBlock

      if (meetingSlotBlock) {
        const newBlock: BookMeetingBlock = {
          ...meetingSlotBlock,
          errorCode: event.payload.errorCode,
        }

        parsedEvents = parsedEvents.map((block, index) => {
          if (index === lastMeetingIndex) {
            return newBlock
          }
          return block
        })
      }
    } else {
      if (
        event.type === 'ConversationSessionExpired' ||
        event.type === 'DecodingFailure' ||
        event.type === 'MeetingSchedulingRequested' ||
        event.type === 'SystemConversationAssigned' ||
        event.type === 'SystemConversationReassigned' ||
        event.type === 'SystemRepsHaveFailedToRespond' ||
        event.type === 'SystemConversationReassignedFromTeam' ||
        event.type === 'FormData' ||
        event.type === 'MeetingSchedulingFailed' ||
        event.type === 'MeetingSlotSelectedWithoutReservation' ||
        event.type === 'MeetingSlotSelectedWithReservation' ||
        event.type === 'MeetingSlotSelectionCancelled' ||
        event.type === 'NodeExecutionTimedout' ||
        event.type === 'WaitingForRepHasTimedout' ||
        event.type === 'InternalServerError' ||
        event.type === 'SystemConversationDeadline' ||
        event.type === 'SetupPiperAiChat' ||
        event.type === 'AiResponse' ||
        event.type === 'NodeExecutionResumed' ||
        event.type === 'RepSentMessage' ||
        event.type === 'Start'
      ) {
        return
      }
      parsedEvents.push(event)
    }
  })

  return parsedEvents
}

/**
 * Based on marker eventId, this function will apply its status into the correspondent events. Then,
 * it will remove the marker from the list of events.
 */
const applyMarkers = (events: EventsMap) => {
  const { marker = [], other = [] } = R.groupBy(Object.values(events), event =>
    event?.type === 'Marker' ? 'marker' : 'other'
  )

  const latestMarkers = marker.reduce<
    Record<
      string,
      { authorId: string; roomId: string; status: 'displayed' | 'failed'; timestamp: string }
    >
  >((map, event) => {
    if (!event?.id || event.type !== 'Marker') {
      return map
    }
    const key = `${event.roomId}_${event.author}_${event.status}`
    const existingMarker = map[key]
    if (!existingMarker || existingMarker.timestamp < event.timestamp) {
      map[key] = {
        authorId: event.author,
        roomId: event.roomId,
        timestamp: event.timestamp,
        status: event.status,
      }
    }
    return map
  }, {})

  const latestMarkersByRoom = R.groupBy(R.values(latestMarkers), marker => marker.roomId)

  return other.reduce<Record<string, ParsedPayloadIn>>((acc, event) => {
    if (!event?.id || event.type === 'Marker') {
      return acc
    }
    const markers = latestMarkersByRoom[event.roomId] ?? []
    if (markers.length === 0) {
      acc[event.id] = event
      return acc
    }

    acc[event.id] = { ...event }

    const eventToModify = acc[event.id]
    for (const marker of markers) {
      if (event.timestamp <= marker.timestamp && eventToModify) {
        eventToModify.statuses = {
          ...eventToModify.statuses,
          [marker.authorId]: marker.status,
        }
      }
    }
    return acc
  }, {})
}

/** Parses chat markers into event status and ConversationData into blocks */
export const useAssembledBlocksAndMarkers = (
  events: EventsMap | undefined,
  guestId: string | undefined,
  botId: string | undefined
) => {
  const sortedEventsWithAppliedMarkers = useMemo(() => {
    if (!events) {
      return []
    }
    const result = Object.values(applyMarkers(events) ?? {})
    // We need to sort from oldest to newest because AcceptedInput events need to come after the input they are accepting
    const timestampSortedEvents = result.sort((a, b) => a.timestamp.localeCompare(b.timestamp))
    const sessions = timestampSortedEvents.reduce<Array<Array<ParsedPayloadIn>>>(
      (acc, event) => {
        const lastSession = acc[acc.length - 1]
        if (event.type === 'SystemMessage' && event.payload.type === 'ConversationSessionExpired') {
          lastSession?.push(event)
          acc.push([])
        } else {
          lastSession?.push(event)
        }
        return acc
      },
      [[]]
    )
    const sortedEvents = sessions.flatMap(session =>
      [...session].sort((a, b) => {
        if ('envelopeId' in a && 'envelopeId' in b && a.envelopeId && b.envelopeId) {
          return a.envelopeId - b.envelopeId
        }
        return a.timestamp.localeCompare(b.timestamp)
      })
    )
    return sortedEvents
  }, [events])

  const assembledBlocks = useMemo(() => {
    if (!botId) {
      return []
    }

    return assembleBlocks(sortedEventsWithAppliedMarkers, guestId, botId)
  }, [sortedEventsWithAppliedMarkers, guestId, botId])

  return assembledBlocks
}
